import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/core/lib/queryKeys";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Badge } from "@/shared/ui/badge";
import { Separator } from "@/shared/ui/separator";
import { toast } from "sonner";
import { Cpu, Factory, GitBranch, Layers, Tag, Zap } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { createDatapoint, createFieldMapping } from "@/core/api/data-sources";

interface FieldMappingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  factoryId: string;
  dataSourceId: string;
  bucketName: string;
  measurement: string;
  fieldName: string;
  fieldType?: string;
}

interface Area {
  id: string;
  name: string;
  color: string;
}

interface Line {
  id: string;
  area_id: string;
  name: string;
}

interface Station {
  id: string;
  line_id: string;
  name: string;
}


interface UnitOption {
  id: string;
  name: string;
  symbol: string;
  unit_type: string | null;
}

interface ModuleOption {
  id: string;
  name: string;
  display_name: string;
}

interface ExposedValueDefinition {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  module_id: string;
}

export const FieldMappingDialog = ({
  open,
  onOpenChange,
  factoryId,
  dataSourceId,
  bucketName,
  measurement,
  fieldName,
  fieldType,
}: FieldMappingDialogProps) => {
  const queryClient = useQueryClient();
  
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedExposedValueId, setSelectedExposedValueId] = useState<string | null>(null);
  const [targetType, setTargetType] = useState<'factory' | 'area' | 'line' | 'station'>('factory');
  const [selectedAreaId, setSelectedAreaId] = useState<string>('');
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const [selectedStationId, setSelectedStationId] = useState<string>('');

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setDisplayName(fieldName);
      setSelectedUnitId(null);
      setSelectedExposedValueId(null);
      setTargetType('factory');
      setSelectedAreaId('');
      setSelectedLineId('');
      setSelectedStationId('');
    }
  }, [open, fieldName]);

  // Fetch factory structure
  const { data: areas = [] } = useQuery({
    queryKey: ['factory-areas', factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('factory_areas')
        .select('id, name, color')
        .eq('factory_id', factoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as Area[];
    },
    enabled: open && !!factoryId,
  });

  const { data: lines = [] } = useQuery({
    queryKey: ['production-lines', factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('production_lines')
        .select('id, area_id, name')
        .eq('factory_id', factoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as Line[];
    },
    enabled: open && !!factoryId,
  });

  const { data: stations = [] } = useQuery({
    queryKey: ['stations', factoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('stations')
        .select('id, line_id, name')
        .eq('factory_id', factoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as Station[];
    },
    enabled: open && !!factoryId,
  });

  const { data: units = [] } = useQuery({
    queryKey: ['units'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('units')
        .select('id, name, symbol, unit_type')
        .order('name');
      if (error) throw error;
      return data as UnitOption[];
    },
    enabled: open,
  });

  const { data: modules = [] } = useQuery({
    queryKey: ['module-registry'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('module_registry')
        .select('id, name, display_name')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as ModuleOption[];
    },
    enabled: open,
  });

  const { data: exposedValues = [] } = useQuery({
    queryKey: ['exposed-value-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('core')
        .from('exposed_value_definitions')
        .select('id, name, display_name, description, module_id')
        .order('display_name');
      if (error) throw error;
      return data as ExposedValueDefinition[];
    },
    enabled: open,
  });

  const exposedValuesByModule = useMemo(() => {
    return exposedValues.reduce((acc, definition) => {
      const list = acc.get(definition.module_id) ?? [];
      list.push(definition);
      acc.set(definition.module_id, list);
      return acc;
    }, new Map<string, ExposedValueDefinition[]>());
  }, [exposedValues]);

  const selectedUnit = units.find((unit) => unit.id === selectedUnitId) ?? null;
  const selectedExposedValue = exposedValues.find(
    (definition) => definition.id === selectedExposedValueId
  );

  // Filter lines/stations based on selection
  const filteredLines = selectedAreaId 
    ? lines.filter(l => l.area_id === selectedAreaId) 
    : lines;
  const filteredStations = selectedLineId 
    ? stations.filter(s => s.line_id === selectedLineId) 
    : stations;

  const resolveTenantId = async () => {
    const { data, error } = await supabase
      .schema('core')
      .from('data_sources')
      .select('tenant_id')
      .eq('id', dataSourceId)
      .maybeSingle();
    if (error) throw error;
    if (!data?.tenant_id) throw new Error('Data source not found');
    return data.tenant_id;
  };


  const inferDatatype = (rawType?: string) => {
    if (!rawType) return null;
    const normalized = rawType.toLowerCase();
    if (normalized.includes('bool')) return 'boolean';
    if (normalized.includes('int') || normalized.includes('float') || normalized.includes('double') || normalized.includes('decimal') || normalized.includes('number')) {
      return 'number';
    }
    if (normalized.includes('char') || normalized.includes('string')) return 'string';
    return null;
  };

  // Create mapping mutation
  const createMapping = useMutation({
    mutationFn: async () => {
      const tenantId = await resolveTenantId();

      const { data: existingDatapoint, error: datapointError } = await supabase
        .schema('core')
        .from('datapoints')
        .select('id')
        .eq('data_source_id', dataSourceId)
        .eq('bucket_name', bucketName)
        .eq('measurement_name', measurement)
        .eq('field_name', fieldName)
        .maybeSingle();
      if (datapointError) throw datapointError;

      const datapoint = existingDatapoint
        ? { id: existingDatapoint.id }
        : await createDatapoint({
            tenant_id: tenantId,
            data_source_id: dataSourceId,
            bucket_name: bucketName,
            measurement_name: measurement,
            field_name: fieldName,
            display_name: displayName.trim(),
            description: null,
            datatype: inferDatatype(fieldType) ?? undefined,
            unit_id: selectedUnitId ?? undefined,
            is_active: true,
          });

      const fieldMapping = await createFieldMapping({
        tenant_id: tenantId,
        factory_id: factoryId,
        datapoint_id: datapoint.id,
        area_id: targetType !== 'factory' ? selectedAreaId || null : null,
        line_id: ['line', 'station'].includes(targetType) ? selectedLineId || null : null,
        station_id: targetType === 'station' ? selectedStationId || null : null,
        is_active: true,
      });

      if (selectedExposedValueId) {
        const { error: mappingError } = await supabase
          .schema('core')
          .from('datapoint_exposed_value_mapping')
          .insert({
            tenant_id: tenantId,
            field_mapping_id: fieldMapping.id,
            exposed_value_id: selectedExposedValueId,
          });
        if (mappingError) throw mappingError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.fieldMappings(factoryId) });
      toast.success('Field mapping created');
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error('Failed to create mapping');
      console.error(error);
    },
  });

  const handleSave = () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }
    if (targetType === 'area' && !selectedAreaId) {
      toast.error('Please select an area');
      return;
    }
    if (targetType === 'line' && !selectedLineId) {
      toast.error('Please select a line');
      return;
    }
    if (targetType === 'station' && !selectedStationId) {
      toast.error('Please select a station');
      return;
    }
    createMapping.mutate();
  };

  // Get target icon
  const getTargetIcon = () => {
    switch (targetType) {
      case 'area': return Layers;
      case 'line': return GitBranch;
      case 'station': return Cpu;
      default: return Factory;
    }
  };
  const TargetIcon = getTargetIcon();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-warning" />
            Map Field to Factory Structure
          </DialogTitle>
          <DialogDescription>
            Connect this InfluxDB field to a factory element
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Zap className="h-4 w-4 text-warning" />
              Main information
            </div>

            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground mb-1">Source Field</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{bucketName}</Badge>
                <span className="text-muted-foreground">/</span>
                <Badge variant="outline">{measurement}</Badge>
                <span className="text-muted-foreground">/</span>
                <Badge className="bg-primary/10 text-primary border-primary/20">{fieldName}</Badge>
              </div>
              {fieldType && (
                <p className="text-xs text-muted-foreground mt-1">Type: {fieldType}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Assembly Line Power"
              />
            </div>

            <div className="space-y-2">
              <Label>Unit</Label>
              <Select value={selectedUnitId ?? "none"} onValueChange={(value) => setSelectedUnitId(value === "none" ? null : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value="none">No unit</SelectItem>
                  {units.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.symbol} · {unit.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Use the units catalog for consistent reporting across modules.
              </p>
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <TargetIcon className="h-4 w-4 text-primary" />
              Mapping
            </div>

            <div className="space-y-3">
              <Label>Map to</Label>
              <div className="grid grid-cols-4 gap-2">
                {(['factory', 'area', 'line', 'station'] as const).map((type) => {
                  const icons = {
                    factory: Factory,
                    area: Layers,
                    line: GitBranch,
                    station: Cpu,
                  };
                  const labels = {
                    factory: 'Factory',
                    area: 'Area',
                    line: 'Line',
                    station: 'Station',
                  };
                  const Icon = icons[type];
                  return (
                    <Button
                      key={type}
                      type="button"
                      variant={targetType === type ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setTargetType(type);
                        if (type === 'factory') {
                          setSelectedAreaId('');
                          setSelectedLineId('');
                          setSelectedStationId('');
                        }
                      }}
                      className="gap-1 px-2"
                    >
                      <Icon className="h-3 w-3" />
                      <span className="text-xs">{labels[type]}</span>
                    </Button>
                  );
                })}
              </div>

              {targetType !== 'factory' && (
                <Select value={selectedAreaId} onValueChange={(v) => {
                  setSelectedAreaId(v);
                  setSelectedLineId('');
                  setSelectedStationId('');
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {areas.map((area) => (
                      <SelectItem key={area.id} value={area.id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: area.color }}
                          />
                          {area.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {['line', 'station'].includes(targetType) && selectedAreaId && (
                <Select value={selectedLineId} onValueChange={(v) => {
                  setSelectedLineId(v);
                  setSelectedStationId('');
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select line" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {filteredLines.map((line) => (
                      <SelectItem key={line.id} value={line.id}>
                        <div className="flex items-center gap-2">
                          <GitBranch className="h-3 w-3 text-chart-2" />
                          {line.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {targetType === 'station' && selectedLineId && (
                <Select value={selectedStationId} onValueChange={setSelectedStationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select station" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60 overflow-y-auto">
                    {filteredStations.map((station) => (
                      <SelectItem key={station.id} value={station.id}>
                        <div className="flex items-center gap-2">
                          <Cpu className="h-3 w-3 text-success" />
                          {station.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <Separator />

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Tag className="h-4 w-4 text-primary" />
              Data definition
            </div>

            <div className="space-y-2">
              <Label>Exposed value</Label>
              <Select
                value={selectedExposedValueId ?? "none"}
                onValueChange={(value) => setSelectedExposedValueId(value === "none" ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select exposed value" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  <SelectItem value="none">No exposed value</SelectItem>
                  {modules.flatMap((module) =>
                    (exposedValuesByModule.get(module.id) ?? []).map((definition) => (
                      <SelectItem key={definition.id} value={definition.id}>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{definition.display_name}</span>
                          <span className="text-xs text-muted-foreground">{module.display_name}</span>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedExposedValue?.description ?? "Link this datapoint to a module exposed value."}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={createMapping.isPending}>
            {createMapping.isPending ? 'Creating...' : 'Create Mapping'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
